import { Animation, AnimationBuilder, createAnimation } from '@ionic/angular';

/** Wie weit die eintretende Seite hereinrutscht. */
const SLIDE_PX = 28;

const DURATION_MS = 260;

/**
 * Seitenwechsel ohne Loch in der Mitte.
 *
 * **Das Problem.** Die vorherige Fassung (bis 21.09.2026 als `myTransition` in
 * app.component.ts) hielt die neue Seite die ersten 40 % der Laufzeit auf
 * Deckkraft 0 - bei 450 ms also 180 ms lang - waehrend die alte in 200 ms
 * ausblendete. Auf dev Bild fuer Bild gemessen:
 *
 * ```
 * ms=  0   alt 1.00   neu 0.00      <- alte Seite steht noch voll da
 * ms= 89   alt 0.41   neu 0.00
 * ms=139   alt 0.16   neu 0.07      <- 79 % der Flaeche ist blanker Body
 * ms=206   alt 0.00   neu 0.37
 * ```
 *
 * Erst bleibt die alte Seite scheinbar stehen, dann loest sich alles kurz in
 * den Seitenhintergrund auf. Genau das sieht man als Aufflackern - am Desktop
 * deutlicher, weil dort mehr Flaeche betroffen ist.
 *
 * **Die Loesung.** Es bewegt sich immer nur die obere Seite, die untere bleibt
 * durchgehend deckend. Damit ist die Flaeche in jedem Bild voll bedeckt, und
 * aus dem Loch wird eine saubere Ueberblendung. Welche oben liegt, haengt an
 * der Richtung: vorwaerts bekommt die neue Seite den hoeheren z-index, zurueck
 * behaelt ihn die alte.
 *
 * **Voraussetzung** ist der deckende Seitenhintergrund aus `global.scss`
 * (`ion-router-outlet > .ion-page`). Ohne ihn ist die untere Seite
 * durchsichtig, und die Ueberblendung zeigt wieder den Body statt der Seite
 * darunter.
 */
export const meloPageTransition: AnimationBuilder = (
  _baseEl: HTMLElement,
  opts: any
): Animation => {
  const entering: HTMLElement | undefined = opts?.enteringEl;
  const leaving: HTMLElement | undefined = opts?.leavingEl;

  const root = createAnimation().duration(DURATION_MS).easing('ease-out');

  if (!entering) return root;

  // Ohne Vorgaenger gibt es nichts zu ueberblenden - der erste Seitenaufbau
  // soll nicht auch noch einblenden.
  if (!leaving) {
    return root.addAnimation(
      createAnimation().addElement(entering).fromTo('opacity', 1, 1)
    );
  }

  const goingBack = opts?.direction === 'back';
  const below = goingBack ? entering : leaving;
  const above = goingBack ? leaving : entering;

  // Die untere Seite steht still und deckt.
  //
  // Der Wert wird trotzdem gesetzt und nicht einfach weggelassen: Ionic haengt
  // der eintretenden Seite `ion-page-invisible` an (opacity 0), und nur ein
  // Inline-Wert aus der Animation sticht das.
  root.addAnimation(
    createAnimation().addElement(below).fromTo('opacity', 1, 1)
  );

  // Die obere Seite kommt herein beziehungsweise geht hinaus.
  root.addAnimation(
    createAnimation()
      .addElement(above)
      .keyframes(
        goingBack
          ? [
              { offset: 0, opacity: '1', transform: 'translateY(0)' },
              { offset: 1, opacity: '0', transform: `translateY(${SLIDE_PX}px)` },
            ]
          : [
              { offset: 0, opacity: '0', transform: `translateY(${SLIDE_PX}px)` },
              { offset: 1, opacity: '1', transform: 'translateY(0)' },
            ]
      )
  );

  return root;
};
